/* eslint-disable @typescript-eslint/no-non-null-assertion */
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useProductionContextTransposeHotkey } from "hooks/hotkeys";
import { Period } from "hooks/periodIndex";
import { useIsMobile } from "hooks/useIsMobile";
import React, { use } from "react";
import { MultiSelect } from "src/components/common/inputs/multiSelect";
import {
  ChefGroup,
  MaterialRead,
  MixMetadata,
  MixSummary,
  NameBasedPeriodMaterialSummary,
  ProductGroup,
  ProductionMixResultRead,
} from "src/store/api/generatedApi";
import { CopyPlanButton } from "./copyButton";
import {
  buildProductionGroups,
  buildProductionMixes,
  PlanGroup,
  PlanMixes,
} from "./productionContent";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";

export enum Measure {
  Percentage = "mass_fraction",
  Mass = "mass",
  SmallMass = "small_mass",
}

export enum Group {
  Product = "product",
  Chef = "chef",
}

enum ContextEmpty {
  True,
}

export type ProductionVariant = "interactive" | "static";

const EMPTY_GROUP_NAME_BY_MIX_ID: Record<number, string> = {};

interface Context {
  group: Group;
  measure: Measure;
  period: Period;
  steelGrades: Set<string>;
  periodMaterialSummaries: NameBasedPeriodMaterialSummary[];
  groups: PlanGroup[];
  mixes: PlanMixes;
  grouping: Record<number, ProductGroup> | Record<number, ChefGroup>;
  isTransposed: boolean;
  isStatic: boolean;
  hideEmptyMaterialRows: boolean;
  mixColumnWidthPx: number | undefined;
}

const OptionContext = React.createContext<ContextEmpty | Context>(
  ContextEmpty.True
);

const useOptions = () => {
  const context = use(OptionContext);
  if (context === ContextEmpty.True) {
    throw new Error(`Option context not set`);
  }
  return context;
};

export const useGroupOption = () => {
  const { group } = useOptions();
  return group;
};

export const useMeasureOption = () => {
  const { measure } = useOptions();
  return measure;
};

export const usePeriodOption = () => {
  const { period } = useOptions();
  return period;
};

export const useSteelGradesOption = () => {
  const { steelGrades } = useOptions();
  return steelGrades;
};

export const useGroups = () => {
  const { groups } = useOptions();
  return groups;
};

export const useMixes = () => {
  const { mixes } = useOptions();
  return mixes;
};
export const usePeriodMaterialSummaries = () => {
  const { periodMaterialSummaries } = useOptions();
  return periodMaterialSummaries;
};

export const useGrouping = () => {
  const { grouping } = useOptions();
  return grouping;
};

export const useIsTransposed = () => {
  const { isTransposed } = useOptions();
  return isTransposed;
};

export const useIsStatic = () => {
  const { isStatic } = useOptions();
  return isStatic;
};

export const useHideEmptyMaterialRows = () => {
  const { hideEmptyMaterialRows } = useOptions();
  return hideEmptyMaterialRows;
};

export const useMixColumnWidthPx = () => {
  const { mixColumnWidthPx } = useOptions();
  return mixColumnWidthPx;
};

interface Props {
  productGroups: ProductGroup[] | null;
  chemistryGroups: ChefGroup[];
  hasProductGroups: boolean;
  mixResults: ProductionMixResultRead[];
  productGroupResults: ProductionMixResultRead[];
  mixSummaries: MixSummary[];
  planPeriodMaterialSummaries: NameBasedPeriodMaterialSummary[];
  materials: MaterialRead[];
  period: number;
  mixMetadata: MixMetadata[];
  variant?: ProductionVariant;
  hideEmptyMaterialRows?: boolean;
  mixColumnWidthPx?: number;
}

export const ProductionContext = ({
  productGroups,
  chemistryGroups,
  mixResults,
  productGroupResults,
  mixSummaries,
  planPeriodMaterialSummaries,
  materials,
  children,
  hasProductGroups,
  period,
  mixMetadata,
  variant = "interactive",
  hideEmptyMaterialRows = false,
  mixColumnWidthPx,
}: React.PropsWithChildren<Props>) => {
  const isStatic = variant === "static";
  const { default_mix_breakdown: defaultMixBreakdown } = useTenantData();
  const theme = useTheme();
  const isMobile = useIsMobile();

  const { t } = useTenantTranslation();

  const buttonRef = React.useRef(null);

  const [isTransposed, setIsTransposed] = useProductionContextTransposeHotkey();
  const [group, setGroup] = React.useState(Group.Chef);
  const [measure, setMeasure] = React.useState<Measure>(() => {
    switch (defaultMixBreakdown) {
      case "percentage":
        return Measure.Percentage;
      case "mass":
        return Measure.Mass;
      case "small_mass":
        return Measure.SmallMass;
    }
  });

  const [steelGrades, setSteelGrades] = React.useState(() => new Set<string>());
  const [areSettingsOpen, setAreSettingsOpen] = React.useState(false);

  const periodMixSummaries = React.useMemo(
    () => mixSummaries.filter((summary) => summary.period === period),
    [mixSummaries, period]
  );

  // A mix name can differ from its chef group name, so the mix summary gives
  // the chef group of each mix.
  const { grouping, results, groupNameByMixId } = React.useMemo(
    () =>
      group === Group.Product && productGroups
        ? {
            grouping: productGroups,
            results: productGroupResults,
            groupNameByMixId: EMPTY_GROUP_NAME_BY_MIX_ID,
          }
        : {
            grouping: chemistryGroups,
            results: mixResults,
            groupNameByMixId: Object.fromEntries(
              periodMixSummaries.map((summary) => [
                summary.mix_id,
                summary.chef_group_name,
              ])
            ),
          },
    [
      group,
      productGroups,
      productGroupResults,
      chemistryGroups,
      mixResults,
      periodMixSummaries,
    ]
  );

  const groupingByName = React.useMemo(
    () => Object.fromEntries(grouping.map((group) => [group.name, group])),
    [grouping]
  );

  const groupIdOf = React.useCallback(
    (result: ProductionMixResultRead) =>
      groupingByName[groupNameByMixId[result.mix.id] ?? result.name]!.id!,
    [groupingByName, groupNameByMixId]
  );

  const materialsByName = React.useMemo(
    () =>
      Object.fromEntries(
        materials.map((material) => [material.name, material])
      ),
    [materials]
  );

  // mix_number and customer_mix_id are per (period, chef group); the same mix id
  // can recur across periods, so resolve both within the current period.
  const mixNumberByMixId = React.useMemo(
    () =>
      Object.fromEntries(
        periodMixSummaries.map((summary) => [
          summary.mix_id,
          summary.mix_number,
        ])
      ),
    [periodMixSummaries]
  );

  const customerMixIdByMixId = React.useMemo(
    () =>
      Object.fromEntries(
        mixMetadata
          .filter((metadata) => metadata.period === period)
          .map((metadata) => [metadata.mix_id, metadata.customer_mix_id])
      ),
    [mixMetadata, period]
  );

  const periodMaterialSummaries = planPeriodMaterialSummaries.filter(
    (summary) => summary.period === period
  );

  const mixes = React.useMemo(
    () => buildProductionMixes(results, groupIdOf, materialsByName),
    [results, groupIdOf, materialsByName]
  );

  const groups = React.useMemo(
    () =>
      buildProductionGroups(
        results,
        groupIdOf,
        mixNumberByMixId,
        customerMixIdByMixId
      ),
    [results, groupIdOf, mixNumberByMixId, customerMixIdByMixId]
  );

  const context: Context = React.useMemo(
    () => ({
      group,
      measure,
      period: period as Period,
      steelGrades,
      periodMaterialSummaries,
      groups,
      mixes,
      grouping: typeSafeObjectFromEntries(
        grouping.map((group) => [group.id!, group])
      ),
      isTransposed,
      isStatic,
      hideEmptyMaterialRows,
      mixColumnWidthPx,
    }),
    [
      group,
      measure,
      period,
      steelGrades,
      periodMaterialSummaries,
      groups,
      mixes,
      grouping,
      isTransposed,
      isStatic,
      hideEmptyMaterialRows,
      mixColumnWidthPx,
    ]
  );
  const units = useUnitsFormatter(false);

  return (
    <>
      {!isStatic && !isMobile && (
        <Stack direction="row" justifyContent="space-between">
          <Stack
            direction="row"
            gap={2}
            alignItems="start"
            justifyContent="flex-start"
          >
            <MultiSelect
              sx={{ width: 250 }}
              options={relevantSteelGrades(
                group,
                productGroups,
                chemistryGroups,
                groups
              )}
              getOptionId={(option) => option}
              getOptionLabel={(option) => option}
              values={[...steelGrades]}
              setValues={(steelGradesArray) => {
                setSteelGrades(new Set(steelGradesArray));
              }}
              label={t("searchForSteelGrade")}
              small
            />
          </Stack>
          <Stack direction="row" alignItems="start">
            {productGroups !== null ? (
              <CopyPlanButton
                results={results}
                percentageBreakdown={measure === Measure.Percentage}
              />
            ) : null}
            <Button
              startIcon={<SettingsIcon />}
              variant="text"
              onClick={() => {
                setAreSettingsOpen((areOpen) => !areOpen);
              }}
              ref={buttonRef}
            >
              {t("configuration")}
            </Button>
            <Popover
              anchorEl={buttonRef.current}
              open={areSettingsOpen}
              onClose={() => {
                setAreSettingsOpen(false);
              }}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              sx={{
                borderRadius: 1,
              }}
            >
              <Stack sx={{ padding: 2, gap: 2, width: 150 }}>
                <FormControl sx={{ gap: 0.5 }}>
                  <FormLabel>{t("units")}</FormLabel>
                  <ToggleButtonGroup
                    color="primary"
                    size="small"
                    value={measure}
                    exclusive
                    onChange={(_, measure) => {
                      if (measure !== null) {
                        setMeasure(measure as Measure);
                      }
                    }}
                  >
                    <ToggleButton
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderColor: theme.palette.grey[100],
                      }}
                      value={Measure.Percentage}
                    >
                      {units(Measure.Percentage, false)}
                    </ToggleButton>
                    <ToggleButton
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderColor: theme.palette.grey[100],
                      }}
                      value={Measure.Mass}
                    >
                      {units(Measure.Mass, false)}
                    </ToggleButton>
                    <ToggleButton
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderColor: theme.palette.grey[100],
                      }}
                      value={Measure.SmallMass}
                    >
                      {units(Measure.SmallMass, false)}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </FormControl>
                {hasProductGroups ? (
                  <FormControl
                    sx={{
                      gap: 0.5,
                      "& .Mui-focused": {
                        color: theme.palette.text.secondary,
                      },
                    }}
                  >
                    <FormLabel>{t("Group by")}</FormLabel>
                    <RadioGroup
                      value={group}
                      onChange={(_, selectedValue) => {
                        const selectedGroup = Object.values(Group).find(
                          (option: string) => option === selectedValue
                        );
                        if (selectedGroup) {
                          setGroup(selectedGroup);
                        }
                      }}
                    >
                      <FormControlLabel
                        value={Group.Chef}
                        sx={{ gap: 0.5, marginLeft: 0 }}
                        control={<Radio size="small" sx={{ padding: 0 }} />}
                        label={t("chemistryGroup")}
                      />
                      <FormControlLabel
                        value={Group.Product}
                        sx={{ gap: 0.5, marginLeft: 0 }}
                        control={<Radio size="small" sx={{ padding: 0 }} />}
                        label={t("productGroup")}
                      />
                    </RadioGroup>
                  </FormControl>
                ) : null}
                <FormLabel
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  {t("transposeTable") + " "}
                  <Switch
                    size="small"
                    checked={isTransposed}
                    onChange={() => {
                      setIsTransposed((transposed) => !transposed);
                    }}
                  />
                </FormLabel>
              </Stack>
            </Popover>
          </Stack>
        </Stack>
      )}
      <OptionContext value={context}>{children}</OptionContext>
    </>
  );
};

const relevantSteelGrades = (
  group: Group,
  productGroups: null | ProductGroup[],
  chemistryGroups: ChefGroup[],
  groups: PlanGroup[]
) => {
  const groupIds = new Set(groups.map((group) => group.groupId));
  const grouping = (() => {
    switch (group) {
      case Group.Product: {
        if (productGroups) {
          return productGroups;
        } else {
          return chemistryGroups;
        }
      }
      case Group.Chef: {
        return chemistryGroups;
      }
    }
  })();
  return grouping
    .filter(({ id }) => groupIds.has(id!))
    .flatMap((group) => group.steel_grades.map(({ name }) => name));
};
